import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {url} from '../helpers/url';
import {SharedSettings} from '../models/shared-settings';
import {PowerBiReportParameters} from '../models/power-bi-report';
import {AuthService} from '@shared/authentication/auth.service';
import {Observable, of, zip} from 'rxjs';
import { PowerBiReport } from '@shared/models/power-bi-report';
import {map} from 'rxjs/operators';


@Injectable({
    providedIn: 'root'
})
export class PowerBiService {

    reportsApi = this.settings.apiBaseUrl + 'reports/';
    categoryRoles = new Map<string, string[]>([
        ['Accounting', ['scs accounting', 'scs admin', 'it', 'developers']],
        ['Management', ['scs regional manager', 'scs site admin', 'scs admin', 'it', 'developers']]
    ]);

    constructor(
        private http: HttpClient,
        private settings: SharedSettings,
        private authService: AuthService) {
    }

    getReportUrl(report: PowerBiReport, params: PowerBiReportParameters[]) {
        const p = [ 'autoAuth=true', url`reportId=${report.reportId}` ];

        if (report.tenantId)
            p.push(url`ctid=${report.tenantId}`);

        if (report.isPaginated && params)
            p.push(...params.map(p => url`rp:${p.rpName}=${p.rpValue}`));

        const u = report.isPaginated
            ? 'https://app.powerbi.com/rdlEmbed'
            : 'https://app.powerbi.com/reportEmbed';

        return u + (p.length ? '?' : '') + p.join('&');
    }

    public getReport(id: string): Observable<PowerBiReport> {
        return this.http.get<PowerBiReport>(this.reportsApi + url`${id}`)
    }

    public getCategories(): Observable<string[]> {
        return this.http.get<string[]>(this.reportsApi);
    }

    public getReportsByCategory(cat: string): Observable<PowerBiReport[]> {
        return this.http.get<PowerBiReport[]>(this.reportsApi + url`category/${cat}`);
    }

    public getReportByName(name: string): Observable<PowerBiReport> {
        return this.http.get<PowerBiReport>(this.reportsApi + url`name/${name}`);
    }

    public getReports(): Observable<PowerBiReport[]> {
        return this.http.get<PowerBiReport[]>(this.reportsApi + url`all`);
    }

    public getReportsByUserRoles(): Observable<PowerBiReport[]> {
        return zip([this.getReports(), this.authService.Roles])
            .pipe(map(([reports, roles]) =>
                reports.filter(r => this.hasAccess(r.category, roles))));
    }

    public canAccessCategory(category: string): Observable<boolean> {
        if (!this.categoryRoles.has(category))
            return of(true);

        return this.authService.Roles.pipe(
            map(roles => this.hasAccess(category, roles)));
    }

    private hasAccess(category: string, roles: string[]): boolean {
        if (!this.categoryRoles.has(category))
            return true;

        return this.categoryRoles.get(category).some(r => roles.includes(r));
    }
}

